import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import React, { useContext } from 'react';
import { createRoot } from 'react-dom/client';
import { DragIcon } from './DragIcon';
import { DragContext } from './DragContextProvider';
import ETreeActionTypes from 'enums/ETreeActionTypes';
import { getDragImage } from './utils';

export const renderComponentToElement = (Component, props, text = '') => {
  const container = document.createElement('div');
  const root = createRoot(container);
  root.render(
    <div className="flex flex-col gap-1">
      <Component {...props} />
      {text && (
        <span className="bg-[#474747] text-white rounded-sm text-lg p-1 whitespace-nowrap opacity-75">
          {text}
        </span>
      )}
    </div>,
  );
  return container;
};

interface Props<T> extends React.ComponentProps<'div'> {
  node: T;
  nodeName: string;
  selectedFiles: T[];
  handleDrop: (nodes: T[], operationType: ETreeActionTypes) => void;
  onDragStart?: (e: React.DragEvent<HTMLDivElement>) => void;
  validateDrop?: (selectedNodes: T[], currentNode: T) => boolean;
  enableDropOnly?: boolean;
  className?: string;
  externalDragHandling?: boolean;
}
let isValidState = true;
let isControlPressed = false;
export function DraggableContainer<T>(props: Props<T>) {
  const {
    node,
    selectedFiles,
    nodeName,
    handleDrop,
    onDragStart = null,
    validateDrop = null,
    enableDropOnly = false,
    className = '',
    externalDragHandling = false,
  } = props;
  const dragContext = useContext(DragContext);
  const { iconRef, tooltipRef } = dragContext;

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    isControlPressed = e.ctrlKey;
    e.stopPropagation();
    if (enableDropOnly) {
      e.preventDefault();
      return;
    }
    if (onDragStart) {
      onDragStart(e);
    }

    /** Handle cases for multiple selection drag drop */
    if (!selectedFiles.includes(node)) {
      if (e.ctrlKey) {
        dragContext.setDraggedNodes([...selectedFiles, node]);
      } else {
        dragContext.setDraggedNodes([node]);
      }
    } else {
      dragContext.setDraggedNodes([...selectedFiles]);
    }

    /** Create drag image */
    const dragImage = getDragImage(externalDragHandling, selectedFiles);
    dragImage.style.position = 'absolute';
    document.body.appendChild(dragImage);
    iconRef.current = dragImage;
    isValidState = true;

    const tooltipIcon = document.createElement('div');
    tooltipIcon.style.position = 'absolute';
    tooltipIcon.style.visibility = 'hidden';
    tooltipIcon.style.background = '#474747';
    tooltipIcon.style.borderRadius = '3px';
    tooltipIcon.style.color = '#FFFFFF';
    tooltipIcon.style.padding = '5px';
    document.body.appendChild(tooltipIcon);
    tooltipRef.current = tooltipIcon;

    /** Set empty drag image to avoid default image */
    const div = document.createElement('div');
    e.dataTransfer.setDragImage(div, -20, 0);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (!validateDrop) {
      return;
    }

    if (validateDrop(dragContext.draggedNodes, node)) {
      e.dataTransfer.dropEffect = 'move';
      /** Change drag icon accordingly if drop is valid at current node */
      if (iconRef?.current) {
        if (!isValidState) {
          /** only chanfge icon if previous state is invalid to avoid flickering effect */
          const dragImage = renderComponentToElement(DragIcon, {
            number: dragContext.draggedNodes.length,
          });
          iconRef.current.replaceChildren(dragImage);
          isValidState = true;
        }
        tooltipRef.current.style.visibility =
          'visible'; /** change tooltip visibility and text */
        tooltipRef.current.innerText = `${
          isControlPressed ? 'Copy to' : 'Move to'
        } [${nodeName}]`;
      }
    } else {
      e.dataTransfer.dropEffect = 'none';
      if (iconRef?.current && isValidState) {
        const dragImage = renderComponentToElement(RMSvgIcon, {
          name: 'DragError',
          height: '2.25rem',
          width: '2.25rem',
        });
        iconRef.current.replaceChildren(dragImage);
        isValidState = false;
      }
      if (tooltipRef?.current) {
        tooltipRef.current.style.visibility = 'visible';
        tooltipRef.current.innerText = `Can't ${isControlPressed ? 'copy' : 'move'} file`;
      }
    }
  };

  const handleDragEnd = (e: React.MouseEvent) => {
    e.stopPropagation();
    /** Remove drag image and tooltip  if not removed in onDrop */
    if (document.body.contains(iconRef.current)) {
      document.body.removeChild(iconRef.current);
    }
    if (document.body.contains(tooltipRef.current)) {
      document.body.removeChild(tooltipRef.current);
    }
  };

  const handleDragMove = (e: React.DragEvent<HTMLDivElement>) => {
    e.stopPropagation();
    /** Set position of drag image and tooltip */
    iconRef.current.style.top = e.clientY + 'px';
    iconRef.current.style.left = e.clientX + 20 + 'px';

    tooltipRef.current.style.top = e.clientY + 40 + 'px';
    tooltipRef.current.style.left = e.clientX + 20 + 'px';
  };

  if (!iconRef) {
    return <>{props.children}</>;
  }

  const handleOnDrop = (e: React.DragEvent<HTMLDivElement>) => {
    /** Remove drag image and tooltip  if not removed in onDragEnd */
    e.stopPropagation();
    if (document.body.contains(iconRef.current)) {
      document.body.removeChild(iconRef.current);
    }
    if (document.body.contains(tooltipRef.current)) {
      document.body.removeChild(tooltipRef.current);
    }
    handleDrop(
      dragContext.draggedNodes,
      isControlPressed ? ETreeActionTypes.Copy : ETreeActionTypes.Cut,
    );
  };

  return (
    <div
      id="draggablecontainer"
      draggable
      onDragStart={handleDragStart}
      onDrag={handleDragMove}
      onDragEnd={handleDragEnd}
      {...(!externalDragHandling && {
        onDragOver: handleDragOver,
        onDrop: handleOnDrop,
      })}
      className={className}
    >
      {props.children}
    </div>
  );
}
